import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Loader } from "lucide-react";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { mouserCreateOrder, getMouserEnvironment } from "actions/mouserAction";
import { saveOrder } from "actions/cartActions";
import { useAuth } from "AuthContext";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { getErrorMessage } from "utils/helper";
import { mouserOrderTypes } from "constants/common";
import SamiSpin from "components/SamiSpin";

const MySwal = withReactContent(Swal);

const MouserModal = ({ modelData, setModelData }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderType, setOrderType] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [poNumber, setPoNumber] = useState("");
  const { clearCartByPartner, clearCartDetailItems, cartDetailItems, cart } = useAuth();
  const [data, setData] = useState({
    shippingMethod: null,
    paymentMethod: null,
  });
  // const [error, setError] = useState({
  //   shippingMethodError: null,
  //   paymentMethodError: null,
  // });

  console.log({ data });

  const handleShippingMethodChange = (value) => {
    setData({ ...data, shippingMethod: value });
  };

  const handlePaymentMethodChange = (value) => {
    setData({ ...data, paymentMethod: value });
  };

  const handleOrderTypeChange = (newValue) => {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    // console.log({ modelData });

    if (!data?.shippingMethod || !data?.paymentMethod || !orderType) {
      setError("Please select all required fields.");
      return;
    }

    setIsLoading(true);
    const mouserEnv = await getMouserEnvironment();

    let isProdOrder = false;
    if (mouserEnv?.status) {
      isProdOrder = mouserEnv?.data?.mouser_order_environment == "1";
    }
    const timeStamp = new Date().getTime();
    const selectedPayment = modelData?.paymentOption?.[parseInt(data?.paymentMethod)];

    const orderPayload = {
      Order: {
        // TODO: In this address has "AddressLocationTypeID" and it should be string
        ShippingAddress: modelData?.orderQueryRes?.data?.OrderInitialize?.ShippingAddress || null,
        OrderType: orderType,
        PrimaryShipping: {
          Code: parseInt(data?.shippingMethod),
        },
        SecondaryShipping: {
          Code: parseInt(data?.shippingMethod),
        },
        IECCode: modelData?.orderQueryRes?.data?.OrderInitialize?.IECCode || null,
        PrimaryFreightAccount: null,
        SecondaryFreightAccount: null,
        Payment: {
          Method: parseInt(selectedPayment?.Method),
          VatInvoiceAddress: null,
          CreditCardLastFourDigits: selectedPayment?.CreditCardLastFourDigits,
          VatAccountNumber: modelData?.paymentData?.VatAccounts?.[0],
          TaxCertificateKey: null,
          TaxStatus: "None",
          PoNumber: poNumber.trim() ? poNumber.trim() : `PO-${timeStamp}`,
          VatInvoiceType: "None",
          BankName: null,
          BankAccountNumber: null,
          UseRfcNumber: false,
          // TODO: this should be boolean
          VatNumberValidForEuShipments: "null",
        },
        CurrencyCode: currency,
        CartKey: modelData?.CartKey,
        LanguageCode: "en-US",
        SubmitOrder: isProdOrder, // INFO: when testing change to false
      },
    };

    console.log("OrderSubmitPayload=>", { orderPayload });
    const orderRes = await mouserCreateOrder({ payload: orderPayload, currency });
    console.log("orderRes=>", orderRes);

    if (
      orderRes.status &&
      (orderRes.data?.ShippingMethod?.Error?.Errors?.length > 0 ||
        orderRes.data?.PaymentMethod?.Error?.Errors?.length > 0 ||
        orderRes.data?.Order?.Errors?.length > 0)
    ) {
      // setError({
      //   shippingMethodError: orderRes?.data?.ShippingMethod?.Error?.Errors?.[0]?.Message,
      //   paymentMethodError: orderRes?.data?.PaymentMethod?.Error?.Errors?.[0]?.Message,
      // });
      setError(getErrorMessage(orderRes?.data));
      setIsLoading(false);
      return;
    }
    console.log("orderRes=>", orderRes);

    if (
      orderRes.status &&
      !orderRes.data?.ShippingMethod?.Error?.Errors?.length &&
      !orderRes.data?.PaymentMethod?.Error?.Errors?.length &&
      !orderRes.data?.Order?.Errors?.length
    ) {
      const bomLinks = (cart || [])
        .filter(
          (item) =>
            item?.bom_item_id &&
            String(item?.api_partner || "").toLowerCase() === "mouser",
        )
        .map((item) => ({
          bom_item_id: item.bom_item_id,
          bom_id: item.bom_id ?? null,
          mfr_part_number: item.part_number ?? null,
          part_number: item.api_partner_part_number ?? null,
        }));
      const saveOrderResponse = await saveOrder({
        mouser_data: {
          ...orderRes?.data?.Order,
          cartDetailItems: cartDetailItems,
        },
        bom_links: bomLinks,
      });
      console.log("saveOrderResponse=>", saveOrderResponse);

       clearCartByPartner("Mouser");
      clearCartDetailItems();

      setModelData({
        ...modelData,
        open: false,
      });
      setData({
        ...data,
        shippingMethod: null,
        paymentMethod: null,
      });
      setError(null);
      await MySwal.fire({
        title: <strong>Mouser Order Success !</strong>,
        html: (
          <i>
            Order created successfully, Your order number is <b>{orderRes?.data?.Order?.OrderID}</b>
          </i>
        ),
        icon: "success",
      });
      navigate("/orders");
    } else {
      // setError({
      //   shippingMethodError: orderRes?.message,
      //   paymentMethodError: orderRes?.message,
      // });
      setError(getErrorMessage(orderRes?.data));
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  return (
    <Dialog
      open={modelData.open}
      onOpenChange={(open) => {
        setModelData({ ...modelData, open });
        setError({
          shippingMethodError: null,
          paymentMethodError: null,
        });
      }}
    >
      <DialogContent className="sm:max-w-[750px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-primary">Shipping Address</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                <b>First Name:</b> {modelData?.shippingAddress?.FirstName}
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">
                <b>Last Name:</b> {modelData?.shippingAddress?.LastName}
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="AddressOne">
                <b>Address Line 1:</b> {modelData?.shippingAddress?.AddressOne}
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="AddressTwo">
                <b>Address Line 2:</b> {modelData?.shippingAddress?.AddressTwo}
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">
                <b>City:</b> {modelData?.shippingAddress?.City}
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">
                <b>Company:</b> {modelData?.shippingAddress?.Company}
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">
                <b>Phone:</b> {modelData?.shippingAddress?.PhoneNumber}
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="postalCode">
                <b>Postal Code:</b> {modelData?.shippingAddress?.PostalCode}
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shippingMethod">
                Shipping Method <span className="text-red-500">*</span>
              </Label>
              <Select value={data?.shippingMethod} onValueChange={handleShippingMethodChange}>
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Shipping Method" />
                </SelectTrigger>
                <SelectContent>
                  {modelData?.shippingOption.map((shipping) => (
                    <SelectItem value={shipping.Code.toString()}>{shipping.Method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="paymentMethod">
                Payment Method <span className="text-red-500">*</span>
              </Label>
              <Select value={data?.paymentMethod} onValueChange={handlePaymentMethodChange}>
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Payment Method" />
                </SelectTrigger>
                <SelectContent>
                  {modelData?.paymentOption.map((payment, index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {payment.Name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="orderType">
                Order Type <span className="text-red-500">*</span>
              </Label>
              <Select value={data?.orderType} onValueChange={(newValue) => setOrderType(newValue)}>
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Order Type" />
                </SelectTrigger>
                <SelectContent>
                  {mouserOrderTypes.map((type) => (
                    <SelectItem value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="poNumber">PO Number <span className="text-gray-400 font-normal">(optional)</span></Label>
              <input
                id="poNumber"
                type="text"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                placeholder={`PO-${new Date().getTime()}`}
                className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="text-sm mb-4">
            <p>Note:</p>
            <p>
              <strong>Rush</strong> - Ship immediately rather than waiting for back-ordered items
            </p>
            <p>
              <strong>Complete</strong> - Wait until all items are in stock, then ship together
            </p>
          </div>
          {/* {error?.shippingMethodError && (
            <div className="text-sm">
              Shipping Method Error:{" "}
              <span className="text-red-500">{error?.shippingMethodError}</span>
            </div>
          )}
          {error?.paymentMethodError && (
            <div className="text-sm mt-3 mb-4">
              Payment Method Error:{" "}
              <span className="text-red-500">{error?.paymentMethodError}</span>
            </div>
          )} */}
          {error && (
            <div className="text-sm mt-3 mb-4 text-red-500">
              Error: <span className="text-red-500">{error}</span>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="secondary"
              className="hover:bg-gray-300 border border-gray-300"
              type="button"
              onClick={() => {
                setModelData({ ...modelData, open: false });
                setError({
                  shippingMethodError: null,
                  paymentMethodError: null,
                });
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading}>
              Checkout {isLoading && <SamiSpin className="w-4 h-4 animate-spin" />}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MouserModal;
